// Sync Engine: orchestrates data flow between Zustand and Supabase
// - hydrate: fetch all user data from Supabase → Zustand
// - push: send local mutations to Supabase
// - subscribe: listen for Zustand changes and push to Supabase
// - realtime: subscribe to couple changes via Supabase Realtime

import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { fromDb, fromDbArray, toDb, STORE_TO_TABLE, ARRAY_KEYS } from '@/lib/mappers';
import { loadQueue, enqueue, dequeue, getQueue } from '@/lib/mutation-queue';
import { useStore } from '@/utils/store';

let unsubscribers = [];
let realtimeChannel = null;
let isInitialized = false;
let isSyncing = false;

// ============================================
// Hydrate: Supabase → Zustand
// ============================================

export async function hydrate() {
  if (!isSupabaseConfigured()) return;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  isSyncing = true;

  try {
    // Fetch all data in parallel (each query returns { data, error })
    const results = await Promise.all([
      supabase.from('transactions').select('*').order('date', { ascending: false }),
      supabase.from('budgets').select('*'),
      supabase.from('goals').select('*'),
      supabase.from('members').select('*'),
      supabase.from('splits').select('*').order('date', { ascending: false }),
      supabase.from('banks').select('*'),
      supabase.from('credits').select('*'),
      supabase.from('income_units').select('*'),
      supabase.from('fixed_costs').select('*'),
      supabase.from('businesses').select('*'),
      supabase.from('projects').select('*').then((r) => r, () => ({ data: null })),
      supabase.from('milestones').select('*').then((r) => r, () => ({ data: null })),
      supabase.from('properties').select('*').then((r) => r, () => ({ data: null })),
      supabase.from('tenant_expenses').select('*').then((r) => r, () => ({ data: null })),
      supabase.from('personal_projects').select('*').then((r) => r, () => ({ data: null })),
      supabase.from('personal_project_items').select('*').then((r) => r, () => ({ data: null })),
      supabase.from('agenda_items').select('*').order('date', { ascending: true }),
      supabase.from('app_settings').select('*').eq('user_id', user.id).single(),
      supabase.from('profiles').select('*').eq('id', user.id).single(),
    ]);

    const [
      { data: transactions },
      { data: budgets },
      { data: goals },
      { data: members },
      { data: splits },
      { data: banks },
      { data: credits },
      { data: incomeUnitsRaw },
      { data: fixedCosts },
      { data: businesses },
      { data: projectsRaw },
      { data: milestonesRaw },
      { data: propertiesRaw },
      { data: tenantExpensesRaw },
      { data: personalProjectsRaw },
      { data: personalProjectItemsRaw },
      { data: agendaItems },
      { data: appSettings },
      { data: profile },
    ] = results;

    // Nest fixed costs inside income units
    const incomeUnits = (incomeUnitsRaw || []).map((iu) => {
      const mapped = fromDb('income_units', iu);
      mapped.fixedCosts = (fixedCosts || [])
        .filter((fc) => fc.income_unit_id === iu.id)
        .map((fc) => fromDb('fixed_costs', fc));
      return mapped;
    });

    // Nest milestones inside projects
    const projects = (projectsRaw || []).map((pr) => {
      const mapped = fromDb('projects', pr);
      mapped.milestones = (milestonesRaw || [])
        .filter((ms) => ms.project_id === pr.id)
        .map((ms) => fromDb('milestones', ms));
      return mapped;
    });

    // Nest items inside personal projects
    const personalProjects = (personalProjectsRaw || []).map((pp) => {
      const mapped = fromDb('personal_projects', pp);
      mapped.items = (personalProjectItemsRaw || [])
        .filter((item) => item.personal_project_id === pp.id)
        .map((item) => fromDb('personal_project_items', item));
      return mapped;
    });

    // Helper: only overwrite local array if DB returned data
    // This prevents wiping local data when a table is new / not yet synced
    const currentState = useStore.getState();
    const mergeArray = (key, dbArray) => {
      if (dbArray && dbArray.length > 0) return dbArray;
      return currentState[key] || [];
    };

    // Build state update
    const state = {
      _fromRemote: true,
      transactions: mergeArray('transactions', fromDbArray('transactions', transactions || [])),
      budgets: mergeArray('budgets', fromDbArray('budgets', budgets || [])),
      goals: mergeArray('goals', fromDbArray('goals', goals || [])),
      members: mergeArray('members', fromDbArray('members', members || [])),
      splits: mergeArray('splits', fromDbArray('splits', splits || [])),
      banks: mergeArray('banks', fromDbArray('banks', banks || [])),
      credits: mergeArray('credits', fromDbArray('credits', credits || [])),
      incomeUnits: mergeArray('incomeUnits', incomeUnits),
      businesses: mergeArray('businesses', fromDbArray('businesses', businesses || [])),
      projects: mergeArray('projects', projects),
      properties: mergeArray('properties', fromDbArray('properties', propertiesRaw || [])),
      tenantExpenses: mergeArray('tenantExpenses', fromDbArray('tenant_expenses', tenantExpensesRaw || [])),
      personalProjects: mergeArray('personalProjects', personalProjects),
      agendaItems: mergeArray('agendaItems', fromDbArray('agenda_items', agendaItems || [])),
    };

    // App settings: MERGE with local state instead of replacing
    // This preserves settings that may not exist in the DB yet
    if (appSettings) {
      state.appSettings = { ...currentState.appSettings, ...fromDb('app_settings', appSettings) };
    }

    // Profile → user in store
    if (profile) {
      state.user = {
        name: profile.name,
        email: profile.email,
        plan: profile.plan,
        currency: profile.currency,
        initials: profile.initials,
        photoUrl: profile.photo_url,
      };
    }

    useStore.setState(state);
  } finally {
    isSyncing = false;
  }
}

// ============================================
// Push: Zustand mutation → Supabase
// ============================================

export async function pushMutation(mutation) {
  if (!isSupabaseConfigured()) return;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  // Get couple_id from profile
  const { data: profile } = await supabase
    .from('profiles')
    .select('couple_id')
    .eq('id', user.id)
    .single();

  const coupleId = profile?.couple_id || null;

  try {
    await executeMutation(mutation, user.id, coupleId);
  } catch (err) {
    console.error('Sync push failed, queuing for retry:', err);
    enqueue(mutation);
  }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function sanitizeUuidArrays(obj) {
  if (obj.member_ids) {
    obj.member_ids = obj.member_ids.filter((id) => UUID_RE.test(id));
  }
  return obj;
}

async function executeMutation(mutation, userId, coupleId) {
  const { table, operation, data, id } = mutation;

  switch (operation) {
    case 'INSERT': {
      const dbData = sanitizeUuidArrays(toDb(table, data));
      dbData.user_id = userId;
      if (coupleId) dbData.couple_id = coupleId;

      // Handle nested fixed costs for income_units
      if (table === 'income_units' && data.fixedCosts) {
        const { fixedCosts, ...unitData } = dbData;
        delete unitData.fixedCosts;
        const { error } = await supabase.from(table).insert(unitData);
        if (error) throw error;

        if (fixedCosts?.length) {
          const costsToInsert = fixedCosts.map((fc) => ({
            ...toDb('fixed_costs', fc),
            income_unit_id: data.id,
            user_id: userId,
          }));
          await supabase.from('fixed_costs').insert(costsToInsert);
        }
        return;
      }

      // Handle nested items for personal_projects
      if (table === 'personal_projects' && data.items) {
        const { items, ...projectData } = dbData;
        delete projectData.items;
        const { error } = await supabase.from(table).insert(projectData);
        if (error) throw error;

        if (items?.length) {
          const itemsToInsert = items.map((item) => ({
            ...toDb('personal_project_items', item),
            personal_project_id: data.id,
            user_id: userId,
          }));
          await supabase.from('personal_project_items').insert(itemsToInsert);
        }
        return;
      }

      // Handle nested milestones for projects
      if (table === 'projects' && data.milestones) {
        const { milestones, ...projectData } = dbData;
        delete projectData.milestones;
        const { error } = await supabase.from(table).insert(projectData);
        if (error) throw error;

        if (milestones?.length) {
          const msToInsert = milestones.map((ms) => ({
            ...toDb('milestones', ms),
            project_id: data.id,
            user_id: userId,
            ...(coupleId ? { couple_id: coupleId } : {}),
          }));
          await supabase.from('milestones').insert(msToInsert);
        }
        return;
      }

      const { error } = await supabase.from(table).insert(dbData);
      if (error) throw error;
      break;
    }

    case 'UPDATE': {
      const dbData = sanitizeUuidArrays(toDb(table, data));
      // Don't send id in the update body
      delete dbData.id;
      delete dbData.user_id;
      delete dbData.couple_id;

      // Handle fixed costs update for income_units
      if (table === 'income_units' && data.fixedCosts) {
        const { fixedCosts, ...rest } = dbData;
        delete rest.fixedCosts;

        if (Object.keys(rest).length > 0) {
          const { error } = await supabase.from(table).update(rest).eq('id', id);
          if (error) throw error;
        }

        // Sync fixed costs: delete all then re-insert
        await supabase.from('fixed_costs').delete().eq('income_unit_id', id);
        if (fixedCosts?.length) {
          const costsToInsert = fixedCosts.map((fc) => ({
            ...toDb('fixed_costs', fc),
            income_unit_id: id,
            user_id: userId,
          }));
          await supabase.from('fixed_costs').insert(costsToInsert);
        }
        return;
      }

      // Handle items update for personal_projects
      if (table === 'personal_projects' && data.items) {
        const { items, ...rest } = dbData;
        delete rest.items;

        if (Object.keys(rest).length > 0) {
          const { error } = await supabase.from(table).update(rest).eq('id', id);
          if (error) throw error;
        }

        await supabase.from('personal_project_items').delete().eq('personal_project_id', id);
        if (items?.length) {
          const itemsToInsert = items.map((item) => ({
            ...toDb('personal_project_items', item),
            personal_project_id: id,
            user_id: userId,
          }));
          await supabase.from('personal_project_items').insert(itemsToInsert);
        }
        return;
      }

      // Handle milestones update for projects
      if (table === 'projects' && data.milestones) {
        const { milestones, ...rest } = dbData;
        delete rest.milestones;

        if (Object.keys(rest).length > 0) {
          const { error } = await supabase.from(table).update(rest).eq('id', id);
          if (error) throw error;
        }

        await supabase.from('milestones').delete().eq('project_id', id);
        if (milestones?.length) {
          const msToInsert = milestones.map((ms) => ({
            ...toDb('milestones', ms),
            project_id: id,
            user_id: userId,
            ...(coupleId ? { couple_id: coupleId } : {}),
          }));
          await supabase.from('milestones').insert(msToInsert);
        }
        return;
      }

      const { error } = await supabase.from(table).update(dbData).eq('id', id);
      if (error) throw error;
      break;
    }

    case 'DELETE': {
      // For income_units, fixed costs are cascade-deleted
      const { error } = await supabase.from(table).delete().eq('id', id);
      if (error) throw error;
      break;
    }

    case 'UPSERT_SETTINGS': {
      const dbData = toDb(table, data);
      dbData.user_id = userId;
      const { error } = await supabase
        .from(table)
        .upsert(dbData, { onConflict: 'user_id' });
      if (error) throw error;
      break;
    }
  }
}

// ============================================
// Flush offline queue
// ============================================

export async function flushQueue() {
  if (!isSupabaseConfigured()) return;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { data: profile } = await supabase
    .from('profiles')
    .select('couple_id')
    .eq('id', user.id)
    .single();

  const coupleId = profile?.couple_id || null;
  const pending = getQueue();

  for (const mutation of pending) {
    try {
      await executeMutation(mutation, user.id, coupleId);
      dequeue(mutation.id);
    } catch (err) {
      console.error('Queue flush failed for mutation:', mutation, err);
      break; // Stop on first failure to maintain order
    }
  }
}

// ============================================
// Zustand subscriber: detect diffs and push
// ============================================

export function startSyncSubscriber() {
  if (!isSupabaseConfigured()) return;

  let prevState = { ...useStore.getState() };

  const unsub = useStore.subscribe((state) => {
    // Skip if this update came from remote (hydrate or realtime)
    if (state._fromRemote || isSyncing) {
      // Reset flag
      if (state._fromRemote) {
        useStore.setState({ _fromRemote: false });
      }
      prevState = { ...state };
      return;
    }

    // Diff each synced key
    for (const key of ARRAY_KEYS) {
      const table = STORE_TO_TABLE[key];
      if (!table) continue;

      const oldArr = prevState[key] || [];
      const newArr = state[key] || [];

      if (oldArr === newArr) continue;

      const oldMap = new Map(oldArr.map((item) => [item.id, item]));
      const newMap = new Map(newArr.map((item) => [item.id, item]));

      // Detect inserts and updates
      for (const [id, item] of newMap) {
        const old = oldMap.get(id);
        if (!old) {
          pushMutation({ table, operation: 'INSERT', data: item, id });
        } else if (old !== item) {
          pushMutation({ table, operation: 'UPDATE', data: item, id });
        }
      }

      // Detect deletes
      for (const [id] of oldMap) {
        if (!newMap.has(id)) {
          pushMutation({ table, operation: 'DELETE', id });
        }
      }
    }

    // App settings (single object)
    if (prevState.appSettings !== state.appSettings) {
      pushMutation({
        table: 'app_settings',
        operation: 'UPSERT_SETTINGS',
        data: state.appSettings,
      });
    }

    // User profile
    if (prevState.user !== state.user) {
      const userChanges = {};
      const prev = prevState.user || {};
      const curr = state.user || {};
      for (const key of ['name', 'email', 'plan', 'currency', 'initials']) {
        if (prev[key] !== curr[key]) userChanges[key] = curr[key];
      }
      if (Object.keys(userChanges).length > 0) {
        pushProfileUpdate(userChanges);
      }
    }

    prevState = { ...state };
  });

  unsubscribers.push(unsub);
}

async function pushProfileUpdate(changes) {
  if (!isSupabaseConfigured()) return;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const dbData = {};
  if (changes.name !== undefined) dbData.name = changes.name;
  if (changes.email !== undefined) dbData.email = changes.email;
  if (changes.plan !== undefined) dbData.plan = changes.plan;
  if (changes.currency !== undefined) dbData.currency = changes.currency;
  if (changes.initials !== undefined) dbData.initials = changes.initials;

  await supabase.from('profiles').update(dbData).eq('id', user.id);
}

// ============================================
// Realtime: couple sync
// ============================================

const REALTIME_TABLES = [
  'transactions', 'budgets', 'goals', 'members', 'splits',
  'banks', 'credits', 'income_units', 'fixed_costs', 'businesses', 'projects',
  'milestones', 'properties', 'tenant_expenses', 'personal_projects',
  'personal_project_items', 'agenda_items',
];

export async function startRealtimeSync() {
  if (!isSupabaseConfigured()) return;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { data: profile } = await supabase
    .from('profiles')
    .select('couple_id')
    .eq('id', user.id)
    .single();

  if (!profile?.couple_id) return;

  stopRealtimeSync();

  realtimeChannel = supabase
    .channel('couple-sync')
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      filter: `couple_id=eq.${profile.couple_id}`,
    }, (payload) => {
      handleRealtimeEvent(payload, user.id);
    })
    .subscribe();
}

// Nested table config: child table → { parentKey, parentIdField, childKey }
const NESTED_TABLES = {
  fixed_costs: { parentKey: 'incomeUnits', parentIdField: 'income_unit_id', childKey: 'fixedCosts' },
  milestones: { parentKey: 'projects', parentIdField: 'project_id', childKey: 'milestones' },
  personal_project_items: { parentKey: 'personalProjects', parentIdField: 'personal_project_id', childKey: 'items' },
};

function handleRealtimeEvent(payload, currentUserId) {
  const { eventType, table, new: newRow, old: oldRow } = payload;

  // Ignore our own changes
  if (newRow?.user_id === currentUserId) return;

  // Handle nested tables (fixed_costs, milestones, personal_project_items)
  const nested = NESTED_TABLES[table];
  if (nested) {
    handleNestedRealtimeEvent(eventType, table, newRow, oldRow, nested);
    return;
  }

  const storeKey = {
    transactions: 'transactions',
    budgets: 'budgets',
    goals: 'goals',
    members: 'members',
    splits: 'splits',
    banks: 'banks',
    credits: 'credits',
    income_units: 'incomeUnits',
    businesses: 'businesses',
    projects: 'projects',
    properties: 'properties',
    tenant_expenses: 'tenantExpenses',
    personal_projects: 'personalProjects',
    agenda_items: 'agendaItems',
  }[table];

  if (!storeKey) return;

  isSyncing = true;

  const state = useStore.getState();

  switch (eventType) {
    case 'INSERT': {
      const mapped = fromDb(table, newRow);
      useStore.setState({
        _fromRemote: true,
        [storeKey]: [...(state[storeKey] || []), mapped],
      });
      break;
    }
    case 'UPDATE': {
      const mapped = fromDb(table, newRow);
      useStore.setState({
        _fromRemote: true,
        [storeKey]: (state[storeKey] || []).map((item) =>
          item.id === mapped.id ? { ...item, ...mapped } : item
        ),
      });
      break;
    }
    case 'DELETE': {
      const deletedId = oldRow?.id;
      if (deletedId) {
        useStore.setState({
          _fromRemote: true,
          [storeKey]: (state[storeKey] || []).filter((item) => item.id !== deletedId),
        });
      }
      break;
    }
  }

  isSyncing = false;
}

function handleNestedRealtimeEvent(eventType, table, newRow, oldRow, config) {
  const { parentKey, parentIdField, childKey } = config;

  isSyncing = true;
  const state = useStore.getState();
  const parents = state[parentKey] || [];

  switch (eventType) {
    case 'INSERT': {
      const mapped = fromDb(table, newRow);
      const parentId = newRow[parentIdField];
      useStore.setState({
        _fromRemote: true,
        [parentKey]: parents.map((p) =>
          p.id === parentId ? { ...p, [childKey]: [...(p[childKey] || []), mapped] } : p
        ),
      });
      break;
    }
    case 'UPDATE': {
      const mapped = fromDb(table, newRow);
      const parentId = newRow[parentIdField];
      useStore.setState({
        _fromRemote: true,
        [parentKey]: parents.map((p) =>
          p.id === parentId
            ? { ...p, [childKey]: (p[childKey] || []).map((c) => c.id === mapped.id ? { ...c, ...mapped } : c) }
            : p
        ),
      });
      break;
    }
    case 'DELETE': {
      const deletedId = oldRow?.id;
      if (deletedId) {
        useStore.setState({
          _fromRemote: true,
          [parentKey]: parents.map((p) => ({
            ...p,
            [childKey]: (p[childKey] || []).filter((c) => c.id !== deletedId),
          })),
        });
      }
      break;
    }
  }

  isSyncing = false;
}

export function stopRealtimeSync() {
  if (realtimeChannel) {
    supabase.removeChannel(realtimeChannel);
    realtimeChannel = null;
  }
}

// ============================================
// Initialize / Teardown
// ============================================

export async function initSync() {
  if (isInitialized) return;
  if (!isSupabaseConfigured()) return;

  loadQueue();
  await hydrate();
  startSyncSubscriber();
  await startRealtimeSync();
  await flushQueue();

  // Listen for online events to flush queue
  window.addEventListener('online', flushQueue);

  isInitialized = true;
}

export function teardownSync() {
  unsubscribers.forEach((unsub) => unsub());
  unsubscribers = [];
  stopRealtimeSync();
  window.removeEventListener('online', flushQueue);
  isInitialized = false;
}
